import { DataType, Form, FormOptions, Section, useNotify } from '@globaleris/ui.library'
import { Box, makeStyles, useTheme } from '@material-ui/core'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  LeadQueryModel,
  LeadQueryRequestModel
} from '../../api/Argentia/endpoints/leads/model/leads'
import {
  QuoteCommentModel,
  QuotePriceModelPatch,
  QuotesModel,
  QuotesModelQry
} from '../../api/Argentia/endpoints/quotes/model/quotes'
import { EnumEstadoOferta } from '../../api/oData/enums/enumEstadoOferta'
import { EnumTipoLead } from '../../api/oData/enums/enumTipoLead'
import Page from '../../components/page/page'
import PageCenterColumn from '../../components/page/pageCenterColumn'
import PageHeader from '../../components/page/pageHeader'
import { CommmentListCard } from '../../components/shared/comments/commentListCard/commentListCard'
import { LoadingCard } from '../../components/shared/loadingCard/loadingCard'
import { StandardCardTooltip } from '../../components/shared/standardCard/standardCardToolTip/standardCardToolTip'
import { envConfig } from '../../config/envConfig'
import { enumToSelectOptionArray } from '../../helpers'
import { useConfirmationDialog } from '../../hooks'
import { NonAuthRoutes } from '../../model'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { getActiveUser, getCustomerDomain } from '../../redux'
import { downloadPriceListByQuote } from '../documents/documents.service'
import { fileDownloadHelper } from '../../helpers/fileDownloadHelper'
import { mapRowQuoteModelToRequest } from '../leads/leads.mapper'
import { queryLead } from '../leads/leads.service'
import { LoadingForm } from '../tanks/components/loadingForm'
import { ActionHeaderButtons } from './components/actionHeaderButtons/actionHeaderButtons'
import { PriceListTable } from './components/priceListTable/priceListTable'
import { useGetQuoteComments } from './quotes.hooks'
import { mapQueryLeadToPricesListDataTable, mapQueryModelToFormData } from './quotes.mapper'
import { PriceModelQryDataTable } from './quotes.model'
import {
  createQuoteComment,
  publishQuoteAsync,
  reEvaluateQuote,
  updateQuote,
  updateQuotePriceList
} from './quotes.service'
import { styles } from './quotes.styles'

interface QuotesPageProps {
  location: {
    state?: {
      quoteId?: number
    }
  }
}

export const QuotesPage: React.FC<QuotesPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'quotes', 'enums'])
  const history = useHistory()
  const { info, error } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const activeUser = getActiveUser()
  const quoteId = props.location.state?.quoteId
  if (!quoteId) {
    history.goBack()
    return <></>
  }
  const {
    isFetching: isFetchingQuoteComments,
    fetchData: quoteComments,
    getQuoteComments
  } = useGetQuoteComments(quoteId)

  const [quoteFormData, setQuoteFormData] = useState<QuotesModelQry | undefined>()
  const [leadQuery, setLeadQuery] = useState<LeadQueryModel | undefined>()
  const [leadType, setLeadType] = useState<EnumTipoLead | undefined>()
  const [priceListDataTable, setPriceListDataTable] = useState<
    PriceModelQryDataTable[] | undefined
  >()

  const [priceListisLoading, setPriceListIsLoading] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  // company info
  const imgUrl = `${envConfig.publicUrl}/favicons/${activeUser.favicon}`

  // #region Handlers Starts

  // ------------------- Action Header Handlers -----------------------------//
  const handlePublishClick = () => {
    if (quoteFormData) {
      if (startDateIsBeforeToday(quoteFormData.startDate)) {
        getConfirmation(ConfirmationDialogs.PUBLISH_QUOTE_DATE_WARNING).then(async () => {
          publishQuote()
          closeConfirmation()
        })
      } else {
        getConfirmation(ConfirmationDialogs.PUBLISH_QUOTE_VALID_DATE).then(async () => {
          publishQuote()
          closeConfirmation()
        })
      }
    }
  }

  const updateQuoteAsync = async () => {
    if (leadQuery && quoteFormData) {
      const request = mapQueryModelToFormData(quoteFormData)
      return await updateQuote(quoteFormData.id, request)
    }
  }

  const handleSaveWithOutPublishClick = async () => {
    await updateQuoteAsync().then(res => {
      res && handleResponse(res.success)
    })
  }

  const handleRejectClick = async () => {
    if (leadQuery && quoteFormData) {
      let request = mapQueryModelToFormData(quoteFormData)
      request.state = EnumEstadoOferta.Baja
      await updateQuote(quoteFormData.id, request).then(res => {
        handleResponse(res.success)
      })
    }
  }

  const handleEvaluateAgainClick = async () => {
    const request = mapRowQuoteModelToRequest({ id: quoteId } as QuotesModel)
    await reEvaluateQuote(request).then(response => {
      if (response.success) {
        info(t('quotes:notifications.reEevaluateQuote.success'))
      } else {
        error(t('quotes:notifications.reEevaluateQuote.error'))
      }
    })
  }

  const handleCancelClick = () => {
    history.goBack()
  }

  const publishQuote = async () => {
    if (leadQuery && quoteFormData) {
      await Promise.all([updateQuoteAsync(), publishQuoteAsync(quoteFormData.id)]).then(res => {
        handleResponse(res.length > 0 && res.every(r => r?.success))
      })
    }
  }

  const handleResponse = (success: boolean) => {
    if (success) {
      leadQuery && history.push(NonAuthRoutes.LeadsFile, { leadId: leadQuery.id })
      info(t('quotes:notifications.updateQuote.success'))
    } else {
      error(t('quotes:notifications.updateQuote.error'))
    }
  }

  // ------------------- PriceList DataTable Handlers -----------------------------//
  const handlePriceListRowManualEdit = (selectedRow: PriceModelQryDataTable) => {
    history.push(NonAuthRoutes.pricingEdit, {
      priceId: selectedRow.id,
      commodity: leadQuery?.commodity,
      priceComponentId: selectedRow.priceComponentId,
      quoteId: quoteId,
      updateLgManual: !selectedRow.lgManual
    })
  }

  const handlePriceListUndoRowManualEdit = (selectedRow: PriceModelQryDataTable) => {
    quoteFormData && updatePriceList(quoteFormData.id, selectedRow.priceComponentId, selectedRow.id)
  }

  const updatePriceList = async (quoteId, priceComponentId, rowPriceId) => {
    let request: QuotePriceModelPatch = {
      quoteId: quoteId,
      lgManual: false
    }
    setPriceListIsLoading(true)
    await updateQuotePriceList(quoteId, priceComponentId, request)
      .then(response => {
        if (response.success) {
          let newTableData = priceListDataTable && [...priceListDataTable]
          newTableData?.map(price => {
            if (price.id === rowPriceId) {
              price.lgManual = false
            }
          })
          setPriceListDataTable(newTableData)
        } else {
          error(t('quotes:notifications.updatePriceList.undoError'))
        }
      })
      .finally(() => {
        setPriceListIsLoading(false)
      })
  }

  const handleDownloadPriceList = async () => {
    await downloadPriceListByQuote(quoteId)
      .then(response => {
        if (response.success && response.data) {
          fileDownloadHelper(response.data.name, response.data)
          info(t('quotes:notifications.downloadPriceList.success'))
        } else {
          error(t('quotes:notifications.downloadPriceList.error'))
        }
      })
      .catch(() => {
        error(t('quotes:notifications.downloadPriceList.error'))
      })
  }

  // -------------------------- Comment List Handlers --------------------------------//

  const handleSaveComment = (textComment: string) => {
    const request: QuoteCommentModel = {
      comment: textComment,
      user: activeUser.name
    }
    createQuoteComment(quoteId, request).then(response => {
      if (response.success) {
        info(t('quotes:notifications.createQuoteComment.success'))
        getQuoteComments()
      } else {
        error(t('quotes:notifications.createQuoteComment.error'))
      }
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const quoteHasDraftState = (quoteState: EnumEstadoOferta | undefined): boolean => {
    if (!quoteState) return false
    return quoteState === EnumEstadoOferta.Borrador
  }

  const leadTypeIsCustom = (leadType: EnumTipoLead | undefined) => {
    return leadType === EnumTipoLead.Personalizado
  }

  const startDateIsBeforeToday = (startDate: Date): boolean => {
    return moment(startDate).isBefore(moment().startOf('day'))
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const quotesFormOptions: FormOptions = {
    elevation: 1,
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('quotes:forms.detail.analytics')
    }
  }

  const quotesFormSections: Section[] = [
    {
      title: t('quotes:forms.detail.title'),
      inputs: [
        {
          name: 'code',
          label: t('quotes:forms.detail.inputs.code'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'description',
          label: t('quotes:forms.detail.inputs.description'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'state',
          label: t('quotes:forms.detail.inputs.state'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumEstadoOferta, 'enums:EnumEstadoOferta'),
          readOnly: true,
          required: true
        },
        {
          name: 'startDate',
          label: t('quotes:forms.detail.inputs.startDate'),
          type: DataType.DATE,
          readOnly: !leadTypeIsCustom(leadType),
          helpText: !leadTypeIsCustom(leadType)
            ? t('quotes:forms.detail.helptext.startDate')
            : undefined,
          required: true
        },
        {
          name: 'endDate',
          label: t('quotes:forms.detail.inputs.endDate'),
          type: DataType.DATE
        },
        {
          name: 'publishDate',
          label: t('quotes:forms.detail.inputs.publicationDate'),
          type: DataType.DATE,
          readOnly: true
        },
        {
          name: 'validityDate',
          label: t('quotes:forms.detail.inputs.effectiveDate'),
          type: DataType.DATE
        }
      ]
    }
  ]
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  useEffect(() => {
    const getQuote = async (quoteId: number) => {
      const queryLeadRequest: LeadQueryRequestModel = {
        quoteId: quoteId
      }
      setIsFetching(true)
      await queryLead(queryLeadRequest)
        .then(res => {
          if (res.success && res.data && res.data.length > 0 && res.data[0].quotes.length > 0) {
            setLeadQuery(res.data[0])
            setQuoteFormData(res.data[0].quotes[0])
            setLeadType(res.data[0].leadType)
            setPriceListDataTable(
              mapQueryLeadToPricesListDataTable(res.data[0].quotes[0].priceList)
            )
          } else {
            error(t('quotes:notifications.queryLeadError'))
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }

    quoteId && getQuote(quoteId)
  }, [])

  // #endregion Hooks Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('quotes:components.detail.title')}>
        <ActionHeaderButtons
          quoteIsDraft={quoteHasDraftState(quoteFormData?.state)}
          onCancelClick={handleCancelClick}
          onEvaluateAgainClick={handleEvaluateAgainClick}
          onRejectClick={handleRejectClick}
          onSaveWithOutPublishClick={handleSaveWithOutPublishClick}
          onPublishClick={handlePublishClick}
          leadTypeIsCustom={leadTypeIsCustom(leadType)}
        />
      </PageHeader>
      <PageCenterColumn>
        {/* ADD BREADCUMBS CLIENT >> LEAD >> QUOTE // TO DO - console */}
        {isFetching ? (
          <>
            <LoadingForm />
            <Box className={classes.card}>
              <LoadingCard />
            </Box>
          </>
        ) : (
          <>
            <Form
              title={t('quotes:components.detail.formTitle')}
              data={quoteFormData}
              options={quotesFormOptions}
              sections={quotesFormSections}
            />
            <Box className={classes.card}>
              <PriceListTable
                data={priceListDataTable ?? []}
                quoteIsDraft={quoteHasDraftState(quoteFormData?.state)}
                isLoading={priceListisLoading}
                onRowManualEditionClick={handlePriceListRowManualEdit}
                onRowUndoManualEditionClick={handlePriceListUndoRowManualEdit}
                onDownloadClick={handleDownloadPriceList}
              />
              <StandardCardTooltip
                style={{ marginLeft: 0 }}
                tooltipTitle={t('quotes:components.priceListTable.cardTooltip.tooltipTitle')}
                tooltipCaption={t('quotes:components.priceListTable.cardTooltip.tooltipCaption')}
              />
            </Box>
          </>
        )}
        <CommmentListCard
          isLoading={isFetchingQuoteComments}
          imgPath={imgUrl}
          comments={quoteComments}
          onSaveComment={handleSaveComment}
          style={{ margin: '0px' }}
        />
      </PageCenterColumn>
    </Page>
  )
}
